<template>
  <vs-popup 
    :active.sync="active"
    title="Edit Storage Account"
    class="edit-account-modal"
  >
    <div class="p-6">
      <form @submit.prevent="updateAccount">
        <!-- Account Info -->
        <div class="mb-4 p-4 bg-grey-light rounded">
          <h4 class="text-sm font-medium mb-2">Account Information</h4>
          <div class="text-sm">
            <div><strong>User:</strong> {{ account.user ? `${account.user.first_name} ${account.user.last_name} (${account.user.email})` : 'N/A' }}</div>
            <div><strong>Account Name:</strong> <code>{{ account.account_name }}</code></div>
            <div><strong>Current Status:</strong> 
              <vs-chip :color="getStatusColor(account.status)" class="ml-2">
                {{ getStatusText(account.status) }}
              </vs-chip>
            </div>
          </div>
        </div>

        <!-- Account Title -->
        <div class="mb-4">
          <label class="block text-sm font-medium mb-2">Account Title *</label>
          <vs-input 
            v-model="form.account_title"
            placeholder="Enter account title..."
            class="w-full"
            :state="errors.account_title ? 'danger' : null"
          />
          <small v-if="errors.account_title" class="text-danger">{{ errors.account_title }}</small>
        </div>

        <!-- Quota -->
        <div class="mb-4">
          <label class="block text-sm font-medium mb-2">Quota (GB) *</label>
          <vs-input 
            v-model="form.quota_gb"
            type="number"
            min="1"
            max="10000"
            placeholder="Enter quota in GB..."
            class="w-full"
            :state="errors.quota_gb ? 'danger' : null"
          />
          <small v-if="errors.quota_gb" class="text-danger">{{ errors.quota_gb }}</small>
          <small class="text-grey">Current: {{ account.quota_gb }} GB | Used: {{ account.used_space_gb || 0 }} GB</small>
        </div>

        <!-- Status -->
        <div class="mb-4">
          <label class="block text-sm font-medium mb-2">Status</label>
          <vs-select 
            v-model="form.status" 
            placeholder="Select status..."
            class="w-full"
            :state="errors.status ? 'danger' : null"
          >
            <vs-select-item value="0" text="Creating" />
            <vs-select-item value="1" text="Active" />
            <vs-select-item value="2" text="Suspended" />
            <vs-select-item value="3" text="Failed" />
            <vs-select-item value="4" text="Deleting" />
          </vs-select>
          <small v-if="errors.status" class="text-danger">{{ errors.status }}</small>
        </div>

        <!-- Usage Information -->
        <div class="mb-4 p-4 bg-primary-light rounded">
          <h4 class="text-sm font-medium mb-2">Current Usage</h4>
          <div class="text-sm">
            <div>Storage Used: {{ account.used_space_gb || 0 }} GB / {{ account.quota_gb }} GB</div>
            <div>Containers: {{ account.containers_count || 0 }}</div>
            <div>Objects: {{ account.objects_count || 0 }}</div>
            <div>Last Sync: {{ account.last_sync_at ? formatDate(account.last_sync_at) : 'Never' }}</div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-end space-x-2">
          <vs-button 
            @click="$emit('close')"
            color="danger"
            type="border"
          >
            Cancel
          </vs-button>
          <vs-button 
            type="submit"
            color="primary"
            :loading="loading"
          >
            Update Account
          </vs-button>
        </div>
      </form>
    </div>
  </vs-popup>
</template>

<script>
export default {
  name: 'EditAccountModal',
  props: {
    account: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      active: true,
      loading: false,
      form: {
        account_title: '',
        quota_gb: 5,
        status: '1'
      },
      errors: {}
    }
  },
  watch: {
    active(newVal) {
      if (!newVal) {
        this.$emit('close')
      }
    }
  },
  mounted() {
    this.initForm()
  },
  methods: {
    initForm() {
      this.form = {
        account_title: this.account.account_title || '',
        quota_gb: this.account.quota_gb || 5,
        status: String(this.account.status || 1)
      }
    },

    async updateAccount() {
      this.loading = true
      this.errors = {}

      try {
        const response = await this.$http.put(`/admin/haio-smart-storage/${this.account.id}`, this.form)
        
        if (response.data.status) {
          this.$vs.notify({
            title: 'Success',
            text: 'Storage account updated successfully',
            color: 'success'
          })
          this.$emit('updated')
        }
      } catch (error) {
        if (error.response && error.response.status === 422) {
          // Validation errors
          const message = error.response.data.message
          this.$vs.notify({
            title: 'Validation Error',
            text: message,
            color: 'danger'
          })
          
          // Set field-specific errors if available
          if (error.response.data.errors) {
            this.errors = error.response.data.errors
          }
        } else {
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to update storage account',
            color: 'danger'
          })
        }
      } finally {
        this.loading = false
      }
    },

    getStatusColor(status) {
      const colors = {
        0: 'warning',  // Creating
        1: 'success',  // Active
        2: 'danger',   // Suspended
        3: 'danger',   // Failed
        4: 'warning'   // Deleting
      }
      return colors[status] || 'primary'
    },

    getStatusText(status) {
      const texts = {
        0: 'Creating',
        1: 'Active',
        2: 'Suspended',
        3: 'Failed',
        4: 'Deleting'
      }
      return texts[status] || 'Unknown'
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.edit-account-modal {
  max-width: 600px;
}
</style>